(() => {
  angular
    .module('gm.common.services')
    .factory('GmyFeatures', GmyFeatures);

  function GmyFeatures(
    $q,
    gmApi,
    GmyVaults,
    btgUser,
    promiseCache
  ) {
    'ngInject';

    return {
      checkCountryPermission,
      checkEnabledAndCountry,
      checkFeatureEnabled,
      checkRedemptionTypeEnabled,
      getAllowedDepositCurrencies,
      getFeatureByName,
      getFeaturesByName,
      getFeatureParameter,
      getFeatureVaults,
      getFeaturesVaults,
      getRedemptionVaults,
      isCCAvailable,
    };

    function checkCountryPermission(featureName) {
      const countryCode = getProfileCountryCode();
      const state = getProfileState();

      function isRulePassed(rule) {
        if ((!rule.country || rule.country === countryCode)
          && (!rule.state || rule.state.toUpperCase() === state)) {
          return true;
        }
        return false;
      }

      return getFeatureByName(featureName)
        .then(({ defaultRegionRestricted, allowedRegions = [], disallowedRegions = [] }) => {
          const allowed = allowedRegions.some(isRulePassed);
          const disallowed = disallowedRegions.some(isRulePassed);
          return !((defaultRegionRestricted && (!allowed || disallowed)) ||
            (!defaultRegionRestricted && disallowed && !allowed));
        });
    }

    function checkEnabledAndCountry(featureName) {
      return $q.all([
        checkCountryPermission(featureName),
        checkFeatureEnabled(featureName)
      ])
      .then(([isAllowed, isEnabled]) => isEnabled && isAllowed);
    }

    function checkFeatureEnabled(featureName) {
      return getFeatureByName(featureName).then(feature => feature && !feature.disabled);
    }

    function checkRedemptionTypeEnabled(redemptionType) {
      return getFeatureByName('redemption')
        .then(redemption => redemption.disabledMethods.indexOf(redemptionType) === -1);
    }

    function getAllowedDepositCurrencies(featureList) {
      const allowedCurrencies = [];

      angular.forEach(featureList.maxDepositAmounts, (_depositAmount, depositCurrency) => {
        allowedCurrencies.push(depositCurrency);
      });
      return allowedCurrencies;
    }

    function getFeatureByName(feature) {
      if (angular.isString(feature)) {
        return getFeatures().then(features => features[feature]);
      }
      return $q.when(false);
    }

    function getFeaturesByName(featureList) {
      if (angular.isArray(featureList)) {
        return getFeatures()
          .then((features) => {
            const featureDetails = [];
            featureList.forEach((feature) => {
              featureDetails.push(features[feature]);
            });
            return featureDetails;
          });
      }
      return $q.when(false);
    }

    function getFeatureParameter(featureName, parameter) {
      return getFeatureByName(featureName).then(feature => feature[parameter]);
    }

    function getFeatures() {
      return promiseCache({
        ttl: 3600000, // 1 hour
        promise: () => gmApi.get('features')
      });
    }

    function getFeatureVaults(feature) {
      return $q.all([
        GmyVaults.getVaults(),
        getFeatureByName(feature),
      ])
      .then(([
        vaults,
        { allowedVaults = [],
          defaultVaultRestricted = false,
          disallowedVaults = [],
        } = {}
      ]) => vaultPermissions(vaults, allowedVaults, defaultVaultRestricted, disallowedVaults));
    }

    function getFeaturesVaults(featureList) {
      return $q.all([
        GmyVaults.getVaults(),
        getFeaturesByName(featureList),
      ])
      .then(([
        vaults,
        features = []
      ]) => {
        let featuresVaults = [];
        features.forEach(({ allowedVaults = [],
          defaultVaultRestricted = false,
          disallowedVaults = [],
        } = {}) => {
          const featureVaults =
            vaultPermissions(vaults, allowedVaults, defaultVaultRestricted, disallowedVaults);
          featuresVaults = featuresVaults.concat(featureVaults);
        });

        const uniqueVaults = [];
        featuresVaults.forEach((vault) => {
          if (uniqueVaults.indexOf(vault) < 0) {
            uniqueVaults.push(vault);
          }
        });

        return uniqueVaults;
      });
    }

    function getProfileCountryCode() {
      const profile = btgUser.getProfile();

      if (profile && profile.countryCode) {
        return profile.countryCode.toUpperCase();
      }

      return null;
    }

    function getProfileState() {
      const profile = btgUser.getProfile();

      if (profile && profile.stateOrProvince) {
        return profile.stateOrProvince.trim().toUpperCase();
      }

      return null;
    }

    function getRedemptionVaults(redemptionType) {
      return $q.all([
        GmyVaults.getVaults(),
        getFeatureByName('redemption'),
      ])
      .then(([
        vaults,
        { disabledVaults = {} },
      ]) => vaultPermissions(vaults, null, false, disabledVaults[redemptionType]));
    }

    function isCCAvailable() {
      const countryCode = getProfileCountryCode();

      return getFeatureByName('deposit')
        .then(deposit => deposit.ccCountryRestricted.indexOf(countryCode) === -1);
    }

    function vaultPermissions(vaults, allowedVaults = [], defaultVaultRestricted,
      disallowedVaults = []) {
      if (defaultVaultRestricted) {
        return vaults.filter(vault => allowedVaults.indexOf(vault.id) !== -1);
      }
      return vaults.filter(vault => disallowedVaults.indexOf(vault.id) === -1);
    }
  }
})();
